import {
  DateInput,
  Form,
  NumberInput,
  SelectInput,
  TextInput,
  UnsavedChangesModal,
  type UnsavedChangesModalProps
} from '@app/components'
import { showMessageNotification, showMessagesNotifications, useSaveEnrollment } from '@app/lib'
import { useSugarContext } from '@app/providers'
import {
  ApiMessage,
  type SugarEnrollment,
  type SugarEnrollmentType,
  type SugarStudentEthnicity,
  type SugarStudentIncomeLevel,
  type SugarStudentRace,
  type SugarYesNo
} from '@app/types'
import { createStyles, Group, Stack, Title } from '@mantine/core'
import { useForm, yupResolver } from '@mantine/form'
import { IconCheck, IconLock } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { find, isArray, isNil, map, toNumber } from 'lodash'
import { type FC, useCallback, useEffect } from 'react'
import * as yup from 'yup'

type FormData = {
  enrollmentEndDate: Date | null
  enrollmentEnrolledHours: number
  enrollmentIepHours: number
  enrollmentStartDate: Date | null
  enrollmentTotalFundedHours: number
  enrollmentType: SugarEnrollmentType | null
  providerLicenseNumber: string
  schoolYearId: string | null
  studentChildUpkId: string
  studentDob: Date | null
  studentDualLanguageLearner: SugarYesNo | null
  studentEthnicity: SugarStudentEthnicity | null
  studentExperiencingHomelessness: SugarYesNo | null
  studentFirstName: string
  studentFosterCare: SugarYesNo | null
  studentHouseholdSize: number
  studentIncomeLevel: SugarStudentIncomeLevel | null
  studentLastName: string
  studentRace: SugarStudentRace | null
}

const useStyles = createStyles(() => ({
  clearDisabled: {
    '&:disabled': {
      backgroundColor: 'unset',
      color: 'unset'
    },
    '&[data-disabled]': {
      backgroundColor: 'unset',
      color: 'unset'
    }
  }
}))

export interface EnrollmentModalProps extends Omit<UnsavedChangesModalProps, 'children'> {
  enrollment: SugarEnrollment | null
}

export const EnrollmentModal: FC<EnrollmentModalProps> = ({ enrollment, onClose, ...props }) => {
  const { classes } = useStyles()
  const queryClient = useQueryClient()
  const { isPending, mutateAsync } = useSaveEnrollment()
  const sugar = useSugarContext()
  const form = useForm<FormData>({
    initialValues: {
      enrollmentEndDate: enrollment?.enrollmentEndDate ? dayjs(enrollment.enrollmentEndDate).toDate() : null,
      enrollmentEnrolledHours: enrollment?.enrollmentEnrolledHours ?? 0,
      enrollmentIepHours: enrollment?.enrollmentIepHours ?? 0,
      enrollmentStartDate: enrollment?.enrollmentStartDate ? dayjs(enrollment.enrollmentStartDate).toDate() : null,
      enrollmentTotalFundedHours: toNumber(enrollment?.enrollmentTotalFundedHours ?? 0),
      enrollmentType: enrollment?.enrollmentType ?? null,
      providerLicenseNumber: enrollment?.providerLicenseNumber ?? '',
      schoolYearId:
        enrollment?.schoolYearId ??
        find(sugar?.options?.enrollmentReportingSchoolYears, 'default')?.value ??
        sugar?.options?.enrollmentReportingSchoolYears?.[0]?.value ??
        null,
      studentChildUpkId: enrollment?.studentChildUpkId ?? '',
      studentDob: enrollment?.studentDob ? dayjs(enrollment.studentDob).toDate() : null,
      studentDualLanguageLearner: enrollment?.studentDualLanguageLearner ?? null,
      studentEthnicity: enrollment?.studentEthnicity ?? null,
      studentExperiencingHomelessness: enrollment?.studentExperiencingHomelessness ?? null,
      studentFirstName: enrollment?.studentFirstName ?? '',
      studentFosterCare: enrollment?.studentFosterCare ?? null,
      studentHouseholdSize: enrollment?.studentHouseholdSize ?? 0,
      studentIncomeLevel: enrollment?.studentIncomeLevel ?? null,
      studentLastName: enrollment?.studentLastName ?? '',
      studentRace: enrollment?.studentRace ?? null
    },
    validate: yupResolver(
      yup.object({
        enrollmentEndDate: yup.date().nullable(),
        enrollmentEnrolledHours: yup.number().min(0, 'Must be a positive number').required('Required'),
        enrollmentIepHours: yup
          .number()
          .transform((value, originalValue) => (originalValue === '' ? undefined : value))
          .integer('Must be a whole number')
          .min(0, 'Must be a positive number')
          .test(
            'iep-within-total',
            'Cannot exceed total UPK-funded hours',
            (value, { parent }) =>
              isNil(parent.enrollmentTotalFundedHours) || (value ?? 0) <= parent.enrollmentTotalFundedHours
          ),
        enrollmentStartDate: yup.date().required('Required'),
        enrollmentTotalFundedHours: yup
          .number()
          .transform((value, originalValue) => (originalValue === '' ? undefined : value))
          .integer('Must be a whole number')
          .required('Required')
          .test(
            'general-award-minimum',
            'Total minus IEP hours must be at least 10',
            (value, { parent }) => (value ?? 0) - (parent.enrollmentIepHours || 0) >= 10
          ),
        enrollmentType: yup.mixed().oneOf(map(sugar?.options?.enrollmentType, 'value')).required('Required'),
        providerLicenseNumber: yup.string().trim().required('Required'),
        schoolYearId: yup.string().uuid().required('Required'),
        studentDob: yup.date().required('Required'),
        studentDualLanguageLearner: yup
          .mixed()
          .oneOf(map(sugar?.options?.studentDualLanguageLearner, 'value'))
          .required('Required'),
        studentEthnicity: yup.mixed().oneOf(map(sugar?.options?.studentEthnicity, 'value')).required('Required'),
        studentExperiencingHomelessness: yup
          .mixed()
          .oneOf(map(sugar?.options?.studentExperiencingHomelessness, 'value'))
          .required('Required'),
        studentFirstName: yup.string().trim().min(2, 'Must be at least 2 characters').required('Required'),
        studentFosterCare: yup.mixed().oneOf(map(sugar?.options?.studentFosterCare, 'value')).required('Required'),
        studentHouseholdSize: yup.number().min(0, 'Must be a positive number').nullable(),
        studentIncomeLevel: yup.mixed().oneOf(map(sugar?.options?.studentIncomeLevel, 'value')).required('Required'),
        studentLastName: yup.string().trim().min(2, 'Must be at least 2 characters').required('Required'),
        studentRace: yup.mixed().oneOf(map(sugar?.options?.studentRace, 'value')).required('Required')
      })
    ),
    validateInputOnChange: true
  })
  const handleClose = useCallback(async () => {
    form.reset()
    onClose()
  }, [form, onClose])
  const isDirty = form.isDirty()
  const isLocked = !!enrollment?.locked
  const isUpdate = !!enrollment?.enrollmentId
  const isValid = form.isValid()

  // The hours rules compare both fields, so re-check both when either changes
  useEffect(() => {
    if (form.isTouched('enrollmentIepHours') || form.isTouched('enrollmentTotalFundedHours')) {
      form.validateField('enrollmentIepHours')
      form.validateField('enrollmentTotalFundedHours')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.enrollmentIepHours, form.values.enrollmentTotalFundedHours])

  return (
    <UnsavedChangesModal
      isChanged={isDirty}
      onClose={handleClose}
      fullScreen
      title={`${enrollment?.enrollmentId ? 'Edit' : 'New'} Enrollment`}
      {...props}
    >
      <Form
        buttonLabel={isLocked ? 'Locked' : 'Save'}
        ButtonIcon={isLocked ? IconLock : IconCheck}
        isSaving={isPending}
        isValid={!isLocked && isDirty && isValid}
        onSubmit={form.onSubmit(
          async ({
            enrollmentEndDate,
            enrollmentEnrolledHours,
            enrollmentIepHours,
            enrollmentStartDate,
            enrollmentTotalFundedHours,
            enrollmentType,
            providerLicenseNumber,
            schoolYearId,
            studentDob,
            studentDualLanguageLearner,
            studentExperiencingHomelessness,
            studentEthnicity,
            studentFosterCare,
            studentHouseholdSize,
            studentIncomeLevel,
            studentRace,
            ...values
          }) => {
            if (!isPending) {
              const { success, messages } = await mutateAsync({
                enrollmentEnrolledHours: enrollmentEnrolledHours || 0,
                enrollmentId: enrollment?.enrollmentId,
                enrollmentIepHours: enrollmentIepHours || 0,
                enrollmentEndDate: enrollmentEndDate ? dayjs(enrollmentEndDate).format('YYYY-MM-DD') : undefined,
                enrollmentStartDate: dayjs(enrollmentStartDate).format('YYYY-MM-DD'),
                enrollmentTotalFundedHours,
                enrollmentType: enrollmentType as SugarEnrollmentType,
                providerLicenseNumber: providerLicenseNumber as string,
                schoolYearId: schoolYearId as string,
                studentDob: dayjs(studentDob).format('YYYY-MM-DD'),
                studentDualLanguageLearner: studentDualLanguageLearner as SugarYesNo,
                studentExperiencingHomelessness: studentExperiencingHomelessness as SugarYesNo,
                studentEthnicity: studentEthnicity as SugarStudentEthnicity,
                studentFosterCare: studentFosterCare as SugarYesNo,
                studentHouseholdSize: studentHouseholdSize ?? undefined,
                studentIncomeLevel: studentIncomeLevel as SugarStudentIncomeLevel,
                studentRace: studentRace as SugarStudentRace,
                ...values
              })

              if (success) {
                await handleClose()
                await queryClient.invalidateQueries({
                  queryKey: ['enrollments']
                })
              }

              if (isArray(messages)) {
                showMessagesNotifications(messages)
              } else {
                showMessageNotification(messages as unknown as ApiMessage)
              }
            }
          }
        )}
      >
        <Stack spacing={0}>
          <Title
            mb='sm'
            order={6}
          >
            Student Information
          </Title>
          <Group
            align='start'
            grow
            position='apart'
            spacing='md'
          >
            <TextInput
              autoCapitalize='words'
              autoComplete='off'
              autoCorrect='off'
              autoFocus
              classNames={{
                input: classes.clearDisabled
              }}
              data-1p-ignore
              disabled={isUpdate || isPending || isLocked}
              label='First Name'
              required={!isUpdate}
              spellCheck={false}
              {...form.getInputProps('studentFirstName')}
            />
            <TextInput
              autoCapitalize='words'
              autoComplete='off'
              autoCorrect='off'
              classNames={{
                input: classes.clearDisabled
              }}
              data-1p-ignore
              disabled={isUpdate || isPending || isLocked}
              label='Last Name'
              required={!isUpdate}
              spellCheck={false}
              {...form.getInputProps('studentLastName')}
            />
          </Group>
          <Group
            align='start'
            grow
            position='apart'
            spacing='md'
          >
            <DateInput
              classNames={{
                input: classes.clearDisabled
              }}
              data-1p-ignore
              label='DOB'
              disabled={isUpdate || isPending || isLocked}
              required={!isUpdate}
              {...form.getInputProps('studentDob')}
            />
            <SelectInput
              data={sugar?.options?.studentRace ?? []}
              data-1p-ignore
              disabled={isPending || isLocked}
              label='Race'
              placeholder='Select'
              required
              selectOnBlur
              {...form.getInputProps('studentRace')}
            />
            <SelectInput
              data={sugar?.options?.studentEthnicity ?? []}
              data-1p-ignore
              disabled={isPending || isLocked}
              label='Ethnicity'
              placeholder='Select'
              required
              selectOnBlur
              {...form.getInputProps('studentEthnicity')}
            />
          </Group>
          <Group
            align='start'
            grow
            position='apart'
            spacing='md'
          >
            <SelectInput
              data={sugar?.options?.studentExperiencingHomelessness ?? []}
              data-1p-ignore
              disabled={isPending || isLocked}
              label='Experiencing Homelessness'
              placeholder='Select'
              required
              selectOnBlur
              {...form.getInputProps('studentExperiencingHomelessness')}
            />
            <SelectInput
              data={sugar?.options?.studentFosterCare ?? []}
              data-1p-ignore
              disabled={isPending || isLocked}
              label='Foster or Kinship Care'
              placeholder='Select'
              required
              selectOnBlur
              {...form.getInputProps('studentFosterCare')}
            />
            <SelectInput
              data={sugar?.options?.studentDualLanguageLearner ?? []}
              data-1p-ignore
              disabled={isPending || isLocked}
              label='Dual Language Learner'
              placeholder='Select'
              required
              selectOnBlur
              {...form.getInputProps('studentDualLanguageLearner')}
            />
          </Group>
          <Group
            align='start'
            grow
            position='apart'
            spacing='md'
          >
            <SelectInput
              data={sugar?.options?.studentIncomeLevel ?? []}
              data-1p-ignore
              disabled={isPending || isLocked}
              label='Income Level'
              placeholder='Select'
              required
              selectOnBlur
              {...form.getInputProps('studentIncomeLevel')}
            />
            <NumberInput
              data-1p-ignore
              disabled={isPending || isLocked}
              label='Household Size'
              min={0}
              {...form.getInputProps('studentHouseholdSize')}
            />
            <TextInput
              autoCapitalize='none'
              autoComplete='off'
              autoCorrect='off'
              data-1p-ignore
              disabled={isPending || isLocked}
              label='UPK ID'
              spellCheck={false}
              {...form.getInputProps('studentChildUpkId')}
            />
          </Group>
          <Title
            my='sm'
            order={6}
          >
            Enrollment Information
          </Title>
          <Group
            align='start'
            grow
            position='apart'
            spacing='md'
          >
            <SelectInput
              data={sugar?.options?.enrollmentType ?? []}
              data-1p-ignore
              disabled={isPending || isLocked}
              label='Enrollment Type'
              placeholder='Select'
              required
              selectOnBlur
              {...form.getInputProps('enrollmentType')}
            />
            <SelectInput
              classNames={{
                input: classes.clearDisabled
              }}
              data={sugar?.options?.enrollmentReportingSchoolYears ?? []}
              data-1p-ignore
              disabled={isPending || isUpdate || isLocked}
              label='School Year'
              placeholder='Select'
              required={!isUpdate}
              selectOnBlur
              {...form.getInputProps('schoolYearId')}
            />
          </Group>
          <Group
            align='start'
            grow
            position='apart'
            spacing='md'
          >
            {isUpdate && (
              <TextInput
                classNames={{
                  input: classes.clearDisabled
                }}
                disabled
                label='Provider Name'
                spellCheck={false}
                value={enrollment?.providerName ?? '-'}
              />
            )}
            <TextInput
              classNames={{
                input: classes.clearDisabled
              }}
              autoCapitalize='none'
              autoComplete='off'
              autoCorrect='off'
              data-1p-ignore
              label='Provider License'
              disabled={isPending || isUpdate || isLocked}
              required={!isUpdate}
              spellCheck={false}
              {...form.getInputProps('providerLicenseNumber')}
            />
          </Group>
          <Group
            align='start'
            grow
            position='apart'
            spacing='md'
          >
            <DateInput
              data-1p-ignore
              disabled={isPending || isLocked}
              label='Start Date'
              required
              {...form.getInputProps('enrollmentStartDate')}
            />
            {isUpdate && (
              <DateInput
                data-1p-ignore
                disabled={isPending || isLocked}
                label='End Date'
                {...form.getInputProps('enrollmentEndDate')}
              />
            )}
          </Group>
          <Group
            align='start'
            grow
            position='apart'
            spacing='md'
          >
            <NumberInput
              data-1p-ignore
              disabled={isPending || isLocked}
              label='Enrolled Hours'
              min={0}
              required
              {...form.getInputProps('enrollmentEnrolledHours')}
            />
            <NumberInput
              data-1p-ignore
              description='Per week, including IEP-funded hours'
              disabled={isPending || isLocked}
              label='Total UPK-Funded Hours'
              min={0}
              required
              {...form.getInputProps('enrollmentTotalFundedHours')}
            />
            <NumberInput
              data-1p-ignore
              disabled={isPending || isLocked}
              label='IEP-Funded Hours'
              min={0}
              {...form.getInputProps('enrollmentIepHours')}
            />
          </Group>
          <Group
            align='start'
            grow
            position='apart'
            spacing='md'
          ></Group>
        </Stack>
      </Form>
    </UnsavedChangesModal>
  )
}
